import React, { useState, useRef } from 'react';
import { Box, Button, Typography, CircularProgress, IconButton } from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
const FoodImageUpload = React.forwardRef(
  (
    {
      onUpload,
      isUploading = false,
      title = 'Upload Food Image',
      description = 'Take a photo or upload an image of your food to get nutritional analysis',
      uploadButtonText = 'Analyze Food Image',
      loadingText = 'Analyzing food image...',
      tips = [
        'Ensure the food is clearly visible',
        'Take photo in good lighting',
        'Include the entire meal in the frame',
        'Avoid blurry or angled shots',
      ],
    },
    ref,
  ) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const fileInputRef = useRef(null);
    const { t } = useTranslation();

    const handleImageSelect = (event) => {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert('Please select a valid image file');
          return;
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          alert('Image size should be less than 5MB');
          return;
        }

        setSelectedImage(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleUpload = () => {
      if (selectedImage && onUpload) {
        onUpload(selectedImage);
      }
    };

    const handleRemoveImage = () => {
      setSelectedImage(null);
      setPreviewUrl('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    // Expose clear method via ref
    React.useImperativeHandle(ref, () => ({
      clearImage: handleRemoveImage,
    }));

    return (
      <Box
        style={{
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          background: '#fff',
          marginBottom: '20px',
        }}
      >
        <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: '600', color: '#333' }}>
          {title}
        </Typography>

        <Typography variant="body2" style={{ marginBottom: '20px', color: '#666' }}>
          {description}
        </Typography>

        {/* Upload Area */}
        <Box
          style={{
            border: '2px dashed #26786A',
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
            background: '#f8f9fa',
            marginBottom: '20px',
            position: 'relative',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {!previewUrl ? (
            <>
              <CloudUpload style={{ fontSize: '48px', color: '#26786A', marginBottom: '16px' }} />
              <Typography variant="h6" style={{ marginBottom: '8px', color: '#333' }}>
                {title}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '20px', color: '#666' }}>
                {t('supportedFormats')}
              </Typography>

              <Box style={{ display: 'flex', gap: '12px' }}>
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    borderColor: '#26786A',
                    color: '#26786A',
                    borderRadius: '25px',
                    textTransform: 'none',
                    fontWeight: '500',
                  }}
                >
                  {t('chooseFile')}
                </Button>
              </Box>
            </>
          ) : (
            <Box style={{ width: '100%' }}>
              <Box style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px',
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                  }}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Box>
            </Box>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
        </Box>

        {/* Upload Button */}
        {selectedImage && (
          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={isUploading}
              startIcon={isUploading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : null}
              style={{
                backgroundColor: '#26786A',
                color: '#fff',
                borderRadius: '25px',
                padding: '12px 32px',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '16px',
              }}
            >
              {/* {isUploading && <CircularProgress size={20} style={{ color: '#fff', marginRight: '8px' }} />} */}
              {isUploading ? loadingText : uploadButtonText}
            </Button>
          </Box>
        )}

        {/* Tips */}
        <Box style={{ marginTop: '20px', padding: '16px', background: '#e8f5e8', borderRadius: '8px' }}>
          <Typography variant="subtitle2" style={{ fontWeight: '600', marginBottom: '8px', color: '#26786A' }}>
            {t('tipsForBetterFoodAnalysis')}
          </Typography>
          <Typography variant="body2" style={{ color: '#26786A' }}>
            {tips.map((tip, index) => (
              <React.Fragment key={index}>
                • {tip}
                <br />
              </React.Fragment>
            ))}
          </Typography>
        </Box>
      </Box>
    );
  },
);

export default FoodImageUpload;
